import { landingImages, LandingSvg } from 'assets';

/**
 * Managed Billing / RCM landing page content.
 * Copy transcribed verbatim from the "TherapyLake Search Landing Pages V_4" spec.
 *
 * PENDING (owner): images (`landingImages.managedBilling*` keys not yet in assets)
 * and section badge SVGs — `BadgeIcon` uses closest existing icons as placeholders.
 */
export const managedBillingFeatures = [
    {
        id: "automated-billing-workflows",
        title: "Automated Billing Workflows from Submission to Payment Posting",
        text: "TherapyLake helps manage the full billing process from start to finish. Our connected workflows support eligibility checks, " +
            "claim submissions, ERAs, payment posting, denials, appeals, and follow-up so your team can spend less time managing billing tasks manually",
        bullets: [
            "TherapyLake helps manage eligibility checks, claim submissions, ERAs, and payment posting",
            "Claims stay connected to sessions, documentation, authorizations, and payer details",
            "Automated workflows help reduce repetitive billing admin work",
            "Your revenue cycle is supported from claim creation through payment follow-up",
        ],
        BadgeIcon: LandingSvg.TaskSvg,
        badgeLabel: 'CLAIMS MANAGED A-Z',
        imageSmall: landingImages.managedBillingWorkflowsImgSmall,
        image: landingImages.managedBillingWorkflowsImg,
    },
    {
        id: "billing-specialists",
        title: "Experienced Billing Specialists Who Help Move Claims Forward",
        text: "When claims need attention, TherapyLake does not leave your team alone with a status code. Our billing support team" +
            " helps review denied, delayed, or appealed claims, contact payers when needed, understand what caused the issue, and work toward a clear resolution.",
        bullets: [
            "Get support from experienced billing specialists who understand ABA billing",
            "Let our team help review denied, delayed, or appealed claims",
            "Receive payer follow-up support when claims need additional attention",
            "Reduce the burden on internal staff when billing issues require extra work",
        ],
        BadgeIcon: LandingSvg.ReceiptItemSvg,
        badgeLabel: 'REAL BILLING SUPPORT',
        imageSmall: landingImages.managedBillingSpecialistsImgSmall,
        image: landingImages.managedBillingSpecialistsImg,
    },
    {
        id: "claim-visibility",
        title: "Stay Aware of Every Claim and Where It Stands",
        text: "Managed billing should never feel like a black box. TherapyLake gives your team visibility into submitted claims, claim status, payment activity, denials, appeals, " +
            "and follow-up work so you can stay informed throughout the process without having to manage every detail yourself.",
        bullets: [
            "See which claims have been submitted and where they stand",
            "Track claim status, payments, denials, appeals, and follow-up activity",
            "Stay informed without relying on scattered spreadsheets or email updates",
            "Keep leadership, billing, and operations aligned around revenue cycle progress",
        ],
        BadgeIcon: LandingSvg.CardEditSvg,
        badgeLabel: 'FULL VISIBILITY',
        imageSmall: landingImages.managedBillingVisibilityImgSmall,
        image: landingImages.managedBillingVisibilityImg,
    },
    {
        id: "cleaner-data",
        title: "We Help Catch Issues Before They Become Claim Problems",
        text: "Many billing problems start before a claim is created. TherapyLake helps your team identify issues earlier by reviewing how authorizations, sessions, services, and scheduling details are connected." +
            " When our team notices something that may create billing problems, we help bring it to your attention so it can be corrected sooner.",
        bullets: [
            "TherapyLake helps confirm authorizations are added correctly before billing begins",
            "Our workflows help catch missing or mismatched details that may lead to claim errors",
            "Our team can point out scheduling or service setup issues when they may affect billing",
            "Cleaner data helps claims move forward with fewer avoidable errors",
        ],
        BadgeIcon: LandingSvg.MathSvg,
        badgeLabel: 'CLEANER DATA BEFORE BILLING',
        imageSmall: landingImages.managedBillingCleanerDataImgSmall,
        image: landingImages.managedBillingCleanerDataImg,
    },
    {
        id: "proactive-rcm",
        title: "More Than Claim Submission",
        text: "TherapyLake’s managed billing support is designed to be proactive, not just reactive. Our team helps with the billing work itself, but we also look for patterns that may be causing avoidable denials, delays, or rework. " +
            "When we notice opportunities to improve the process, we help your team understand what is happening",
        bullets: [
            "TherapyLake helps identify recurring billing issues and workflow gaps",
            "Our team can flag patterns that may be causing avoidable denials or delays",
            "Your practice receives practical feedback when something needs attention",
            "Billing outcomes are supported through software, automation, and human follow-up",
        ],
        BadgeIcon: LandingSvg.ContactPhone,
        badgeLabel: 'PROACTIVE RCM PARTNERSHIP',
        imageSmall: landingImages.managedBillingProactiveRcmImgSmall,
        image: landingImages.managedBillingProactiveRcmImg,
    },
    {
        id: "hipaa-security-2",
        title: "HIPAA-Ready Security & Access Controls",
        text: "Billing workflows involve sensitive client, payer, provider, and payment information. " +
            "TherapyLake is built with security controls that help ABA practices protect data and manage access with confidence.",
        bullets: [
            "Encryption in transit and at rest",
            "Role-based access control to limit who can see what",
            "Audit logs for accountability and compliance",
            "Secure backups to help protect data",
        ],
        BadgeIcon: LandingSvg.SecuritySafeSvg,
        badgeLabel: 'SECURE BY DESIGN',
        imageSmall: landingImages.managedBillingSecurityImgSmall,
        image: landingImages.managedBillingSecurityImg,
    },
];

export const managedBillingFaqs = [
    {
        title: "Does TherapyLake provide managed billing support?",
        description: "Yes. TherapyLake supports ABA billing workflows with connected software and RCM support" +
            " for claims, eligibility, payments, denials, follow-up, and reporting.",
    },
    {
        title: "Can TherapyLake help reduce billing work for internal teams?",
        description: "Yes. TherapyLake helps reduce manual handoffs by connecting billing activity with sessions," +
            " records, claims, payments, and follow-up work in one platform.",
    },
    {
        title: "Is TherapyLake built for ABA revenue teams?",
        description: "Yes. TherapyLake is built around ABA workflows, including scheduling, documentation, claims," +
            " payments, reporting, and RCM support.",
    },
];
